import React from 'react';
import type { FieldProps, FieldRenderProps } from 'react-final-form';
import { Field } from 'react-final-form';

import type { Props as TranslationProps } from '@swf/components/Translation/Translation';
import type { InjectedValidatorProps } from '@swf/core/hoc/withValidator';
import withValidator from '@swf/core/hoc/withValidator';
import usePauseValidator from '@swf/core/hooks/usePauseValidator';
import type { ValuesProp } from '@swf/core/types/Props';
import type { TrackedTranslation } from '@swf/core/types/i18n';

import RawInput from './RawInput';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Props<V = any, T extends HTMLElement = HTMLInputElement>
  extends Omit<React.InputHTMLAttributes<T>, 'id'>,
    InjectedValidatorProps,
    Pick<FieldProps<V, FieldRenderProps<V, HTMLElement>>, 'parse' | 'format' | 'id'>,
    Partial<ValuesProp> {
  error?: boolean;
  helperMessage?: React.ReactNode;
  hideEye?: boolean;
  hideLabel?: boolean;
  icon?: React.FC<React.SVGAttributes<SVGElement>> | React.ReactElement;
  inputRef?: React.RefObject<HTMLTextAreaElement> & React.RefObject<HTMLInputElement>;
  label: string | TrackedTranslation;
  labelClassName?: string;
  labelContent?: React.ReactNode;
  numericKeyboard?: boolean;
  rawValue?: string;
  recordValidator?: InjectedValidatorProps['validator'];
  showValidationTick?: boolean;
  startAdornment?: 'icon' | 'logo' | 'prefix' | 'logoAndPrefix';
  tip?: string;
  tooltip?: TranslationProps[];
  tooltipIconClassName?: string;
  tooltipClassName?: string;
  tooltipHover?: boolean;
  type?: 'date' | 'time' | 'text' | 'textarea' | 'password' | 'number' | 'tel' | 'hidden';
  validationVariant?: Validation;
  value?: string;
}

export enum Validation {
  Inline = 'inline',
  List = 'list',
  Invisible = 'invisible',
  ImmediateInline = 'immediateInline',
  ImmediateList = 'immediateList'
}

const Input: React.FC<Props> = (props) => {
  const { id, name, value, parse, format, recordValidator, validator } = props;
  usePauseValidator();

  return (
    <Field
      id={id}
      name={name || id}
      initialValue={value}
      component="input"
      parse={parse}
      format={format}
      validate={recordValidator || validator}
    >
      {({ input, meta }) => <RawInput {...props} input={input} meta={meta} />}
    </Field>
  );
};

export default withValidator(Input);
